import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, CirclePlus, ImageUp, Trash } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { registerSellersData } from "@/Api/sellers";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getSelectUnities } from "@/Api/unities";
import { getCet } from "@/Api/cep";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormSteps, useStepControl } from "@/components/Form/Steps";
import { TypeUser } from "@/types/userType";
import { useAuth } from "@/hooks/auth";
import { HrFieldsStep, hrFieldsSchema, HR_FIELDS_OMIT_WHEN_EMPTY } from "@/components/HrFieldsStep";

const step1Schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  email: z
    .string({ message: "*requerido" })
    .min(1, "*requerido")
    .email("Email inválido"),
  password: z.string({ message: "*requerido" }).min(1, "*requerido"),
  document: z.string({ message: "*requerido" }).min(1, "*requerido"),
  phone: z.string({ message: "*requerido" }).min(1, "*requerido"),
  photo: z.any(),
  unity_id: z.string({ message: "*requerido" }).min(1, "*requerido"),
});

const step2Schema = z.object({
  street: z.string().min(1, "*requerido").max(255),
  number: z.string().min(1, "*requerido").max(255),
  neighborhood: z.string().min(1, "*requerido").max(255),
  complement: z.string().max(255).optional(),
  state: z.string().min(1, "*requerido").max(255),
  city: z.string().min(1, "*requerido").max(255),
  zip_code: z.string().min(1, "*requerido").max(255),
});

const step3Schema = hrFieldsSchema;

const schema = z.object({
  ...step1Schema.shape,
  ...step2Schema.shape,
  ...step3Schema.shape,
});

const schemas = {
  1: step1Schema,
  2: step2Schema,
  3: step3Schema,
};

type SchemaType = z.infer<typeof schema>;

export const AddBtn = ({ updateFront }) => {
  const user = useAuth();
  const unityId = user?.user?.unity?.[0]?.id || "";
  const userTypeUnitReponsible =
    user?.user?.user_type_id == TypeUser.unity_responsible;
  const stepControls = useStepControl();

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      document: "",
      phone: "",
      photo: null,
      unity_id: userTypeUnitReponsible ? unityId : "",
      street: "",
      number: "",
      neighborhood: "",
      complement: "",
      state: "",
      city: "",
      zip_code: "",
      sector_id: "",
      position_id: "",
      work_schedule_id: "",
      admission_date: "",
    },
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");
  const [isOpen, setIsOpen] = useState(false);
  const [unities, setUnities] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    const loadData = async () => {
      const unitiesResponse = await getSelectUnities();
      
      setUnities(unitiesResponse.data?.response);

      if (userTypeUnitReponsible) {
        form.setValue("unity_id", unityId);
      }
    };
    loadData();
  }, [isOpen]);

  const handleNextStep = async () => {
    const currentSchema = schemas[stepControls.step as keyof typeof schemas];
    const currentStepData = form.getValues();

    try {
      await currentSchema.parseAsync(currentStepData);
      stepControls.setStep(stepControls.step + 1);
    } catch (error) {
      form.trigger();
    }
  };

  const formSubmit = async (data: SchemaType) => {
    try {
      setFormState("loading");
      const formData = new FormData();

      Object.keys(data).forEach((key) => {
        if (key === "photo") {
          if (data[key]?.[0]) {
            formData.append(key, data[key][0]);
          }
          return;
        }
        if (HR_FIELDS_OMIT_WHEN_EMPTY.includes(key) && !data[key]) {
          return;
        }
        formData.append(key, data[key]);
      });

      const { data: apiResponse, ok } = await registerSellersData(formData);
      if (!ok) {
        throw new Error("Erro ao cadastrar vendedor");
      }
      const newData = {
        ...apiResponse?.response,
        is_active: true,
      };
      updateFront((prev) => {
        if (prev?.data.length < 15)
          return {
            ...prev,
            data: [newData, ...prev.data],
          };

        prev?.data.pop();
        return {
          ...prev,
          data: [newData, ...prev.data],
          last_page: prev.last_page + 1,
        };
      });

      setFormState("default");
      setIsOpen(false);
      toast({
        description: `Vendedor criado com sucesso!`,
        action: <Check color="#1edc2b" />,
      });
      stepControls.setStep(1);
      form.reset();
    } catch (error) {
      setFormState("error");
      toast({
        description: `Erro ao criar vendedor`,
        variant: "destructive",
      });

      setTimeout(() => {
        setFormState("default");
      }, 3000);
    }
  };

  const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const cep = e.target.value.replace(/\D/g, "");

    if (cep.length === 8) {
      try {
        const { data } = await getCet(cep);
        if (!data.erro) {
          form.setValue("street", data.logradouro);
          form.setValue("neighborhood", data.bairro);
          form.setValue("city", data.localidade);
          form.setValue("state", data.uf);
        }
      } catch (error) {
        console.error("Erro ao buscar CEP:", error);
      }
    }
  };

  const formWatch = form.watch();

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Adicionar Vendedor
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px] h-[90vh]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Adicionar Vendedor
          </DialogDescription>
        </DialogHeader>

        <FormSteps controls={stepControls}>
          <FormSteps.Header>
            <FormSteps.Indicator step={1}>
              Informações Básicas
            </FormSteps.Indicator>
            <FormSteps.Indicator step={2}>Endereço</FormSteps.Indicator>
            <FormSteps.Indicator step={3}>Informações de RH</FormSteps.Indicator>
          </FormSteps.Header>

          <ScrollArea className="h-[calc(90vh-15rem)] sm:h-[calc(90vh-12rem)] pr-4">
            <Form
              onSubmit={form.handleSubmit(formSubmit)}
              {...form}
              className="space-y-3 p-2"
            >
              <FormSteps.Content step={1}>
                <Form.Row>
                  <Form.Field name="photo">
                    {({ field }) => (
                      <Form.Item className="flex flex-col items-center gap-1">
                        <Form.Label>Perfil:</Form.Label>
                        <div className="flex items-center rounded-full size-32 border border-dashed border-gray-900/25 dark:border-gray-500 relative">
                          <div className="flex text-sm/6 text-gray-600 h-full w-full">
                            <label
                              htmlFor="file-upload"
                              className="w-full h-full flex items-center justify-center cursor-pointer font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 group rounded-full overflow-hidden"
                            >
                              {formWatch?.photo?.length > 0 && (
                                <img
                                  className="absolute top-0 left-0 aspect-square object-contain w-full h-full -z-1 group-hover:brightness-75 transition rounded-full"
                                  src={URL.createObjectURL(
                                    formWatch?.photo?.[0]
                                  )}
                                  alt="profile_image"
                                />
                              )}
                              <span className="flex gap-2 items-center relative z-50">
                                {!(formWatch?.photo?.length > 0) ? (
                                  <ImageUp className="size-7 group-hover:text-blue-700" />
                                ) : (
                                  <ImageUp className="size-7 opacity-0 transition group-hover:opacity-100" />
                                )}
                              </span>
                              <input
                                id="file-upload"
                                type="file"
                                className="sr-only"
                                accept="image/*"
                                onChange={(e) => {
                                  const files = e.target.files;
                                  if (files?.length) {
                                    field.onChange(files);
                                  }
                                }}
                              />
                            </label>
                          </div>
                        </div>
                        {formWatch?.photo?.length > 0 && (
                          <Button
                            type="button"
                            className="h-6 px-0 bg-transparent text-slate font-medium hover:text-red-600 hover:bg-transparent flex gap-1"
                            onClick={() => form.setValue("photo", null)}
                          >
                            <Trash className="size-5" />
                            Remover
                          </Button>
                        )}
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="name">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Nome <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Insira o nome" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="email">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Email <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            placeholder="Insira o email"
                            type="email"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="password">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Senha <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            placeholder="Insira a senha"
                            type="password"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="document">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Documento <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            className="mt-1"
                            typeMask="cpfOrCnpj"
                            placeholder="Insira o número de documentação"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="phone">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Telefone <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            typeMask="phone"
                            placeholder="Insira o telefone"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
                {!userTypeUnitReponsible && (
                  <Form.Row>
                    <Form.Field name="unity_id">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Unidade <Form.Message />
                          </Form.Label>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                            <SelectContent>
                              {unities.map((unity: any) => (
                                <SelectItem
                                  key={unity.id}
                                  value={String(unity.id)}
                                >
                                  {unity.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>
                )}
              </FormSteps.Content>

              <FormSteps.Content step={2}>
                <Form.Row>
                  <Form.Field name="zip_code">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          CEP <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            placeholder="CEP"
                            onChange={(e) => {
                              field.onChange(e);
                              handleCepChange(e);
                            }}
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="street">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Rua <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Rua" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>

                  <Form.Field name="number">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Número <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Número" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="neighborhood">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Bairro <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Bairro" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>

                  <Form.Field name="complement">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Complemento <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Complemento" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="state">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Estado <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Estado" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>

                  <Form.Field name="city">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Cidade <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Cidade" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </FormSteps.Content>

              <FormSteps.Content step={3}>
                <HrFieldsStep isOpen={isOpen} />
              </FormSteps.Content>

              <DialogFooter>
                <div className="flex w-full gap-2">
                  {stepControls.step > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        stepControls.setStep(stepControls.step - 1)
                      }
                    >
                      Voltar
                    </Button>
                  )}

                  {stepControls.step < 3 && (
                    <Button
                      type="button"
                      className={cn("flex-1", colors.bg_primary)}
                      onClick={handleNextStep}
                    >
                      Próximo
                    </Button>
                  )}

                  {stepControls.step === 3 && (
                    <Button
                      type="submit"
                      className={cn("flex-1", colors.bg_primary)}
                      disabled={formState !== "default"}
                    >
                      {formState === "loading" && (
                        <>
                          Adicionando <LoaderSpin />
                        </>
                      )}
                      {formState === "default" && "Adicionar"}
                      {formState === "error" && "Erro ao adicionar"}
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </Form>
          </ScrollArea>
        </FormSteps>
      </DialogContent>
    </Dialog>
  );
};
